import React, { useMemo, useState } from 'react';
import { Alert, Badge, Calendar, Card, Descriptions, Modal, Space, Tag } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useCalendar } from '@/hooks/useManufacturing';
import type { CalendarDay } from '@/services/manufacturing.service';

const ProductionCalendarPage: React.FC = () => {
  const [currentYear, setCurrentYear] = useState(dayjs().year());
  const { data: calendarDays = [], isLoading } = useCalendar(`${currentYear}-01-01`, `${currentYear}-12-31`);

  const days = useMemo(() => (Array.isArray(calendarDays) ? calendarDays : []) as CalendarDay[], [calendarDays]);
  const calendarData = useMemo(() => {
    const map = new Map<string, CalendarDay>();
    days.forEach((day) => {
      const key = String(day.date).slice(0, 10);
      map.set(key, day);
    });
    return map;
  }, [days]);

  const sample = days[0];
  const holidayCalendar = sample?.holiday_calendar;

  const getListData = (value: Dayjs) => {
    const dayData = calendarData.get(value.format('YYYY-MM-DD'));
    if (!dayData) return [];
    if (dayData.is_holiday) {
      return [{ type: 'error' as const, content: dayData.holiday_name || 'Holiday' }];
    }
    if (dayData.is_working_day) {
      const items: Array<{ type: 'success' | 'warning' | 'error' | 'processing'; content: string }> = [
        { type: 'success', content: `${Number(dayData.total_available_hours || 0)} shift hrs` },
      ];
      if (Number(dayData.capacity_utilization) > 90) {
        items.push({ type: 'warning', content: `${dayData.capacity_utilization}% utilized` });
      }
      return items;
    }
    return [{ type: 'processing' as const, content: 'Weekly off' }];
  };

  const dateCellRender = (value: Dayjs) => {
    const listData = getListData(value);
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {listData.map((item, index) => (
          <li key={index}>
            <Badge status={item.type} text={item.content} style={{ fontSize: '11px' }} />
          </li>
        ))}
      </ul>
    );
  };

  const handleSelect = (date: Dayjs) => {
    const dayData = calendarData.get(date.format('YYYY-MM-DD'));
    Modal.info({
      title: date.format('dddd, MMMM D, YYYY'),
      width: 600,
      content: dayData ? (
        <Descriptions column={1} bordered size="small" style={{ marginTop: 20 }}>
          <Descriptions.Item label="Status">
            {dayData.is_holiday ? (
              <Tag color="red" icon={<CloseCircleOutlined />}>Holiday</Tag>
            ) : dayData.is_working_day ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>Working day</Tag>
            ) : (
              <Tag>Weekly off</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Work week">{dayData.work_week_label || dayData.work_week || '—'}</Descriptions.Item>
          <Descriptions.Item label="Holiday calendar">{dayData.holiday_calendar || 'None for this year'}</Descriptions.Item>
          {dayData.is_holiday && (
            <>
              <Descriptions.Item label="Holiday">{dayData.holiday_name}</Descriptions.Item>
              <Descriptions.Item label="Type"><Tag>{dayData.holiday_type}</Tag></Descriptions.Item>
            </>
          )}
          {dayData.is_working_day && (
            <Descriptions.Item label="Shift hours">{Number(dayData.total_available_hours || 0)} hrs</Descriptions.Item>
          )}
        </Descriptions>
      ) : (
        <p style={{ marginTop: 16 }}>This date is outside the loaded year.</p>
      ),
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        loading={isLoading}
        title={
          <Space>
            <CalendarOutlined />
            <span>Production Calendar</span>
          </Space>
        }
      >
        <Alert
          type={holidayCalendar ? 'info' : 'warning'}
          showIcon
          style={{ marginBottom: 16 }}
          message="Working days and holidays come from HRMS"
          description={
            <span>
              Work week: {sample?.work_week_label || sample?.work_week || 'company policy'}.
              {' '}Holiday calendar: {holidayCalendar || `none for ${currentYear}`}.
              {' '}Change them in <Link to="/hr/holidays">Holiday calendars</Link> and HRMS setup.
              The hours on each date are the shift coverage for that day. Overlapping shifts are counted once. A workstation's Capacity/Day is spread across those shifts in Capacity Planning.
            </span>
          }
        />
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Tag color="green"><CheckCircleOutlined /> Working day</Tag>
            <Tag color="red"><CloseCircleOutlined /> Holiday</Tag>
            <Tag>Weekly off</Tag>
          </Space>
        </div>
        <Calendar
          dateCellRender={dateCellRender}
          onSelect={(date, info) => {
            setCurrentYear(date.year());
            if (info.source === 'date') handleSelect(date);
          }}
          onPanelChange={(date) => setCurrentYear(date.year())}
        />
      </Card>
    </div>
  );
};

export default ProductionCalendarPage;
